/**
 * CampaignInputManager - Gates player input to the active campaign's supported
 * input methods (HostCampaignConfig.supportedInputs) in the coordinator path.
 *
 * GitHub #13807: a campaign should be able to declare which input methods it
 * supports (e.g. touch-only games should not grant gamepad). This manager is the
 * coordinator-native enforcement point — it clamps each player peer's
 * `PeerPermissions` so `InputManager` drops disallowed inbound input.
 *
 * Sources of truth and triggers:
 * - PavilionCoordinator owns the campaign config (`getCampaignConfig()`); its
 *   `campaignConfigLoaded` (fires only with authoritative fetched config) and
 *   `campaignCleared` events drive a recompute for all connected players.
 * - PeerCoordinator's `onPlayerJoined` event clamps a newly joined player.
 *
 * Permissions are always recomputed from `DEFAULT_PERMISSIONS.player` (the base
 * the coordinator path assigns on connect), so tightening and loosening both
 * resolve correctly and a cleared campaign restores defaults.
 *
 * After clamping, the player client is notified of its granted access via
 * `setInputAccess` (through `PeerInputAccessNotifier`) so it can bind/unbind the
 * matching capture listeners (notably touch). Without this, a client that froze
 * a default-off touch snapshot would never start capturing touch even though the
 * host is willing to forward it.
 */

import type { PeerCoordinator } from '../PeerCoordinator';
import type { PavilionCoordinator } from '../../pavilion/PavilionCoordinator';
import { DEFAULT_PERMISSIONS } from '../types';
import type { PeerPermissions } from '../types';
import { clampPeerPermissionsToCampaign } from '../campaignPermissions';
import { PeerInputAccessNotifier } from '../peerInputAccess';

export type CampaignInputManagerDeps = {
  peerCoordinator: PeerCoordinator;
  pavilionCoordinator: PavilionCoordinator;
  getHostAuthId: () => string;
};

export class CampaignInputManager {
  private readonly disposers: Array<() => void> = [];
  private readonly notifier: PeerInputAccessNotifier;

  constructor(private readonly deps: CampaignInputManagerDeps) {
    this.notifier = new PeerInputAccessNotifier({
      peerCoordinator: deps.peerCoordinator,
      getHostAuthId: deps.getHostAuthId,
    });
  }

  public init(): void {
    const { peerCoordinator, pavilionCoordinator } = this.deps;

    this.disposers.push(
      pavilionCoordinator.events.campaignConfigLoaded.addListener(() => this.applyToAllPlayers()),
      pavilionCoordinator.events.campaignCleared.addListener(() => this.applyToAllPlayers()),
      peerCoordinator.events.onPlayerJoined.addListener(({ peerId }) => this.applyToPeer(peerId)),
    );
  }

  public dispose(): void {
    for (const dispose of this.disposers) {
      dispose();
    }
    this.disposers.length = 0;
  }

  private supportedInputs() {
    // Only honor config that belongs to the currently assigned campaign.
    // `PavilionCoordinator` leaves the previous campaign's `config` in the store
    // after a clear (which only resets `campaignId`/`reason`) and during the
    // pre-fetch window of a switch. In both cases the stale config must not be
    // applied, otherwise a cleared/switched campaign would keep the old
    // restrictions instead of restoring default player permissions.
    const { campaignId, config } = this.deps.pavilionCoordinator.getCampaignDetails();
    if (!campaignId || config?.campaignId !== campaignId) {
      return undefined;
    }
    return config.supportedInputs;
  }

  private applyToAllPlayers(): void {
    const permissions = this.resolvePermissions();
    this.deps.peerCoordinator.updateAllPlayerPermissions(permissions);
    this.notifier.notifyAllPlayers(permissions);
  }

  private applyToPeer(peerId: string): void {
    const permissions = this.resolvePermissions();
    this.deps.peerCoordinator.updatePeerPermissions(peerId, permissions);
    this.notifier.notifyPeer(peerId, permissions);
  }

  private resolvePermissions(): PeerPermissions {
    return clampPeerPermissionsToCampaign(DEFAULT_PERMISSIONS.player, this.supportedInputs());
  }
}
